import React from 'react';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Sidebar } from './components/Layout/Sidebar';
import { Topbar } from './components/Layout/Topbar';
import { Dashboard } from './pages/Dashboard';
import { GanttView } from './pages/GanttView';
import { CalendarView } from './pages/CalendarView';
import { ProjectBuilder } from './pages/ProjectBuilder';
import { BusinessUnits } from './pages/BusinessUnits';
import { TasksPage } from './pages/Library/TasksPage';
import { WorkflowsPage } from './pages/Library/WorkflowsPage';
import { ProjectsPage } from './pages/Library/ProjectsPage';
import { UsersPage } from './pages/UsersPage';
import { ProfilePage } from './pages/ProfilePage';
import { Login } from './pages/Login';
import { StoreProvider, useStore } from './store/StoreContext';
import SafeIcon from './common/SafeIcon';

const ProtectedLayout = ({ children }) => {
  const { state, logout } = useStore();

  // ONLY show the full screen loader if we have NO user and we are still loading
  // If we have a user, even if loading is true (background sync), we keep the children mounted
  if (state.loading && !state.currentUser) {
    return (
      <div className="h-screen w-screen bg-base-900 flex flex-col items-center justify-center gap-6">
        <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
        <p className="text-gray-500 font-mono text-[10px] uppercase tracking-widest animate-pulse">Connecting to Nest...</p>
      </div>
    );
  }

  if (state.dbError && !state.currentUser) {
    return (
      <div className="h-screen w-screen bg-base-900 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-base-800 border border-base-700 rounded-3xl p-8 text-center shadow-2xl">
          <div className="w-16 h-16 bg-red-400/10 text-red-400 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <SafeIcon name="AlertTriangle" className="w-8 h-8" />
          </div>
          <h2 className="text-xl font-bold mb-2 text-white">Database Setup Required</h2>
          <p className="text-gray-400 text-sm mb-8">
            The application is connected to Supabase, but the necessary tables don't exist yet.
          </p>
          <div className="bg-base-900 rounded-xl p-4 text-left font-mono text-[10px] text-gray-500 mb-8 overflow-hidden break-words">
            Error: {state.dbError}
          </div>
          <button 
            onClick={() => window.location.reload()} 
            className="w-full py-3 bg-primary text-white rounded-xl font-bold hover:bg-primary-hover transition-all mb-4"
          >
            Retry Connection
          </button>
          <button onClick={logout} className="text-xs text-gray-500 hover:text-white uppercase font-bold tracking-widest">
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  if (!state.currentUser) {
    return <Login />;
  }

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-base-900 text-white font-sans selection:bg-primary/30">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0 bg-base-900">
        <Topbar />
        <main className="flex-1 overflow-hidden relative">
          {children}
          {/* Subtle background loader for background syncs */}
          {state.loading && (
            <div className="absolute top-4 right-4 z-50">
              <div className="w-4 h-4 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

const AdminRoute = ({ children }) => {
  const { state } = useStore();
  if (state.currentUser?.role !== 'admin') {
    return <Navigate to="/" replace />;
  }
  return children;
};

function App() {
  return (
    <StoreProvider>
      <HashRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/*" element={
            <ProtectedLayout>
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/gantt" element={<GanttView />} />
                <Route path="/calendar" element={<CalendarView />} />
                <Route path="/builder" element={<ProjectBuilder />} />
                <Route path="/builder/:projectId" element={<ProjectBuilder />} />
                <Route path="/library/tasks" element={<TasksPage />} />
                <Route path="/library/workflows" element={<WorkflowsPage />} />
                <Route path="/library/projects" element={<ProjectsPage />} />
                <Route path="/business-units" element={<BusinessUnits />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/users" element={<AdminRoute><UsersPage /></AdminRoute>} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </ProtectedLayout>
          } />
        </Routes>
      </HashRouter>
    </StoreProvider>
  );
}

export default App;